import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsAccessibility from "highcharts/modules/accessibility";
import { useTheme } from "@mui/material/styles";
import { ICustomer } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";

// Initialize Highcharts modules
HighchartsExporting(Highcharts);
HighchartsAccessibility(Highcharts);

interface ITaxpopup {
  payrollData: ICustomer[];
}

const PayrollDashboard = ({ payrollData }: ITaxpopup) => {
  const classes = useStyles();
  const theme = useTheme();

  // Count policies by productName
  const companyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    payrollData.forEach((item) => {
      const name = item.clientTypeName || "Unknown";
      counts[name] = (counts[name] || 0) + 1;
    });
    return counts;
  }, [payrollData]);

  // Prepare data for bar chart
  const categories = useMemo(() => Object.keys(companyCounts), [companyCounts]);
  const data = useMemo(() => Object.values(companyCounts), [companyCounts]);

  // Highcharts options for bar chart
  const barChartOptions: Highcharts.Options = useMemo(() => ({
    chart: {
      type: "column",
      backgroundColor: theme.palette.cardAdd?.bgColor || "#fff",
      width: undefined, 
    },
    title: {
      text: "Policy Owner by Client Types",
      style: {
        fontSize: "0.9375rem",
        color: theme.palette.heading?.headingColor || "#000",
      },
    },
    xAxis: {
      categories,
      title: { text: null },
      labels: {
        style: {
          color: theme.palette.heading?.headingColor || "#000",
          fontSize: "0.75rem",
        },
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: "Number of Policy Owner",
        style: {
          color: theme.palette.heading?.headingColor || "#000",
          fontSize: "0.75rem",
        },
      },
      labels: {
        style: {
          color: theme.palette.heading?.headingColor || "#000",
        },
      },
    },
    tooltip: {
      pointFormat: "<b>{point.y} Policy Owner</b>",
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          style: {
            color: theme.palette.heading?.headingColor || "#000",
            fontSize: "0.75rem",
          },
        },
      },
    },
    legend: { enabled: false },
    series: [
      {
        type: "bar",
        name: "Policies",
        data,
        color: theme.palette.primary.main,
      },
    ],
    credits: { enabled: false },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            chart: {
              height: 400,
            },
            xAxis: {
              labels: {
                style: { fontSize: "0.6rem" },
              },
            },
          },
        },
      ],
    },
  }), [categories, data, theme]);

  return (
    <div
      style={{
        padding: "10px",
        width: "100%",
        // maxWidth: "900px", // maximum width for dashboard
        margin: "0 auto",  // center horizontally
      }}
    >
      <HighchartsReact highcharts={Highcharts} options={barChartOptions} />
    </div>
  );
};

export default PayrollDashboard;
